import React, { useState, useEffect } from "react";
import { CalendarDays, ChevronDown } from "lucide-react";
import { DateInput } from "./DateInput";

const toISO = (d) => d.toISOString().split("T")[0];

const getPresetDates = (preset) => {
  const today = new Date();
  const todayISO = toISO(today);

  switch (preset) {
    case "oggi":
      return { start: todayISO, end: todayISO };
    case "ieri": {
      const y = new Date(today);
      y.setDate(y.getDate() - 1);
      const yISO = toISO(y);
      return { start: yISO, end: yISO };
    }
    case "settimana": {
      const dow = (today.getDay() + 6) % 7; // Monday = 0
      const mon = new Date(today);
      mon.setDate(today.getDate() - dow);
      const sun = new Date(mon);
      sun.setDate(mon.getDate() + 6);
      return { start: toISO(mon), end: toISO(sun) };
    }
    case "mese": {
      const first = new Date(today.getFullYear(), today.getMonth(), 1);
      const last = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return { start: toISO(first), end: toISO(last) };
    }
    case "mese_scorso": {
      const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const last = new Date(today.getFullYear(), today.getMonth(), 0);
      return { start: toISO(first), end: toISO(last) };
    }
    case "30g": {
      const d = new Date(today);
      d.setDate(d.getDate() - 29);
      return { start: toISO(d), end: todayISO };
    }
    case "90g": {
      const d = new Date(today);
      d.setDate(d.getDate() - 89);
      return { start: toISO(d), end: todayISO };
    }
    case "anno": {
      return {
        start: `${today.getFullYear()}-01-01`,
        end: `${today.getFullYear()}-12-31`,
      };
    }
    default:
      return null;
  }
};

const detectPreset = (dataInizio, dataFine) => {
  const presets = ["oggi", "ieri", "settimana", "mese", "mese_scorso", "30g", "90g", "anno"];
  for (const p of presets) {
    const d = getPresetDates(p);
    if (d && d.start === dataInizio && d.end === dataFine) return p;
  }
  return "personalizzato";
};

const PRESETS = [
  { key: "oggi",        label: "Oggi" },
  { key: "ieri",        label: "Ieri" },
  { key: "settimana",   label: "Questa settimana" },
  { key: "mese",        label: "Questo mese" },
  { key: "mese_scorso", label: "Mese scorso" },
  { key: "30g",         label: "Ultimi 30 giorni" },
  { key: "90g",         label: "Ultimi 90 giorni" },
  { key: "anno",        label: "Anno corrente" },
  { key: "personalizzato", label: "Personalizzato" },
];

const DateRangeFilter = ({ dataInizio, dataFine, onRangeChange, className = "" }) => {
  const [activePreset, setActivePreset] = useState(() => detectPreset(dataInizio, dataFine));
  const [showCustom, setShowCustom] = useState(false);

  // Sync preset detection when parent changes dates
  useEffect(() => {
    const detected = detectPreset(dataInizio, dataFine);
    setActivePreset(detected);
    setShowCustom(detected === "personalizzato");
  }, [dataInizio, dataFine]);

  const handlePreset = (key) => {
    setActivePreset(key);
    if (key === "personalizzato") {
      setShowCustom(true);
      return;
    }
    setShowCustom(false);
    const dates = getPresetDates(key);
    if (dates) onRangeChange(dates.start, dates.end);
  };

  const formatDisplay = (iso) => {
    if (!iso) return "";
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
  };

  const activeLabel = PRESETS.find(p => p.key === activePreset)?.label || "Periodo";

  return (
    <div className={`${className}`} data-testid="date-range-filter">
      {/* I filtri vanno a capo su schermi stretti, senza nascondere le date. */}
      <div className="flex flex-wrap items-center gap-2 pb-1">
        <CalendarDays className="h-4 w-4 text-muted-foreground shrink-0" />
        {PRESETS.map(({ key, label }) => {
          const isActive = key === activePreset;
          return (
            <button
              key={key}
              onClick={() => handlePreset(key)}
              data-testid={`preset-${key}`}
              className={`
                shrink-0 rounded-full px-3.5 py-1.5 text-xs font-medium whitespace-nowrap
                transition-all duration-200 border
                ${isActive
                  ? "bg-primary text-primary-foreground border-primary shadow-sm"
                  : "bg-background text-muted-foreground border-border hover:bg-muted hover:text-foreground"}
              `}
            >
              {label}
              {key === "personalizzato" && isActive && (
                <ChevronDown className={`inline h-3 w-3 ml-1 transition-transform ${showCustom ? "rotate-180" : ""}`} />
              )}
            </button>
          );
        })}
      </div>

      {/* Riepilogo range attivo (solo per preset non-personalizzato) */}
      {activePreset !== "personalizzato" && (
        <p className="text-[11px] text-muted-foreground mt-1.5 ml-6" data-testid="active-range-label">
          {formatDisplay(dataInizio)} → {formatDisplay(dataFine)}
        </p>
      )}

      {/* Input personalizzato — slide-in */}
      {showCustom && (
        <div
          className="flex flex-col sm:flex-row sm:items-center gap-3 mt-3 ml-0 sm:ml-6 animate-in fade-in slide-in-from-top-1 duration-150"
          data-testid="custom-range-inputs"
        >
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <span className="text-xs text-muted-foreground font-medium w-5 shrink-0">Da</span>
            <div className="flex-1 sm:w-[160px]">
              <DateInput value={dataInizio} onChange={(v) => onRangeChange(v, dataFine)} label="Data inizio" />
            </div>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <span className="text-xs text-muted-foreground font-medium w-5 shrink-0">A</span>
            <div className="flex-1 sm:w-[160px]">
              <DateInput value={dataFine} onChange={(v) => onRangeChange(dataInizio, v)} label="Data fine" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangeFilter;
